import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../services/api';
import {
  HiOutlineX,
  HiOutlineFilm,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineClock,
  HiOutlineCalendar,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

function UserStatsModal({ isOpen, user, onClose }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('usage');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      loadStats();
    }
    // Reset tab when modal reopens
    if (isOpen) {
      setActiveTab('usage');
    }
  }, [isOpen, user]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getStats(user.id);
      setStats(response.data);
    } catch (error) {
      toast.error(t('users.errorLoadingStats'));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return t('users.notAvailable');
    try {
      // Parse ISO string - backend sends UTC time
      const date = new Date(dateStr);

      // Subtract 3 hours to get Brazil time (GMT-3 = UTC - 3)
      const brazilTime = new Date(date.getTime() - (3 * 60 * 60 * 1000));

      // Format date using local methods (not UTC methods)
      const day = String(brazilTime.getDate()).padStart(2, '0');
      const month = String(brazilTime.getMonth() + 1).padStart(2, '0');
      const year = brazilTime.getFullYear();
      const hours = String(brazilTime.getHours()).padStart(2, '0');
      const minutes = String(brazilTime.getMinutes()).padStart(2, '0');
      const seconds = String(brazilTime.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--fixed" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', height: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="modal-header">
          <h2>{t('users.statistics')} - {user?.username}</h2>
          <button className="modal-close" onClick={onClose}>
            <HiOutlineX size={20} />
          </button>
        </div>

        {/* Tabs inside modal-body */}
        <div className="modal-body" style={{ paddingBottom: '24px', flex: 1, overflowY: 'auto' }}>
          <div className="tabs tabs--no-arrow" style={{ marginBottom: '20px' }}>
            <button
              className={`tab ${activeTab === 'usage' ? 'active' : ''}`}
              onClick={() => setActiveTab('usage')}
            >
              {t('users.usageStatistics')}
            </button>
            <button
              className={`tab ${activeTab === 'media' ? 'active' : ''}`}
              onClick={() => setActiveTab('media')}
            >
              {t('users.moviesAndSeries')}
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : stats && activeTab === 'usage' ? (
            <div className="user-stats">
              {/* Stat rows using simple div-based layout */}
              <div className="user-stats__grid">
                <div className="user-stats__item">
                  <HiOutlineClock size={18} />
                  <span className="user-stats__label">{t('users.lastLoginDate')}</span>
                  <span className="user-stats__value">
                    {stats.last_login ? formatDate(stats.last_login) : t('users.neverLoggedIn')}
                  </span>
                </div>
                <div className="user-stats__item">
                  <HiOutlineCalendar size={18} />
                  <span className="user-stats__label">{t('users.accountCreated')}</span>
                  <span className="user-stats__value">
                    {stats.created_at ? formatDate(stats.created_at) : t('users.notAvailable')}
                  </span>
                </div>
                <div className="user-stats__item">
                  <HiOutlineFilm size={18} />
                  <span className="user-stats__label">{t('users.totalItems')}</span>
                  <span className="user-stats__value">{stats.total_items}</span>
                </div>
                <div className="user-stats__item">
                  <span className="user-stats__label">{t('users.moviesCount')}</span>
                  <span className="user-stats__value">{stats.movies_count}</span>
                </div>
                <div className="user-stats__item">
                  <span className="user-stats__label">{t('users.tvShowsCount')}</span>
                  <span className="user-stats__value">{stats.tv_count}</span>
                </div>
                <div className="user-stats__item">
                  <HiOutlineEye size={18} />
                  <span className="user-stats__label">{t('users.watchedCount')}</span>
                  <span className="user-stats__value">{stats.watched_count}</span>
                </div>
                <div className="user-stats__item">
                  <HiOutlineEyeOff size={18} />
                  <span className="user-stats__label">{t('users.unwatchedCount')}</span>
                  <span className="user-stats__value">{stats.unwatched_count}</span>
                </div>
              </div>
            </div>
          ) : stats && activeTab === 'media' ? (
            <div className="user-stats">
              {/* Movies Section */}
              <h4 style={{ marginBottom: '8px', fontWeight: '600' }}>
                {t('users.totalMovies')}: {stats.movies_count}
              </h4>
              {stats.movies.length > 0 ? (
                <div className="user-stats__list">
                  {stats.movies.map((movie, idx) => (
                    <div key={idx} className="user-stats__list-item">
                      <span>{movie.title}</span>
                      <span className={`badge ${movie.watched ? 'badge-success' : 'badge-error'}`}>
                        {movie.watched ? t('users.watched') : t('users.notWatched')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>{t('users.noItems')}</p>
              )}

              {/* TV Series Section */}
              <h4 style={{ marginTop: '20px', marginBottom: '8px', fontWeight: '600' }}>
                {t('users.totalTvShows')}: {stats.tv_count}
              </h4>
              {stats.tv_shows.length > 0 ? (
                <div className="user-stats__list">
                  {stats.tv_shows.map((show, idx) => {
                    const badgeClass = show.watched ? 'badge-success' : show.in_progress ? 'badge-warning' : 'badge-error';
                    const badgeText = show.watched ? t('users.watched') : show.in_progress ? t('users.inProgress') : t('users.notWatched');
                    return (
                      <div key={idx} className="user-stats__list-item">
                        <span>{show.title}</span>
                        <span className={`badge ${badgeClass}`}>
                          {badgeText}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)' }}>{t('users.noItems')}</p>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer with close button */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserStatsModal;
